import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
	GameBulkPatch,
	GameListParams,
	GameSortField,
	SortOrder,
} from "../../../admin-api/games";
import { adminApi } from "../../../api-client";
import type { Game } from "../../../types";
import { queryKeys } from "../../query-keys";

/** The admin games list defaults to one screen of rows; the server clamps at 100. */
export const DEFAULT_GAMES_PAGE_SIZE = 20;

export type UseGamesParams = GameListParams & {
	search?: string;
	sortBy?: GameSortField;
	order?: SortOrder;
};

export function useGames(params: UseGamesParams = {}) {
	const { limit = DEFAULT_GAMES_PAGE_SIZE, offset = 0, search, sortBy, order, ...rest } = params;
	return useQuery({
		placeholderData: keepPreviousData,
		queryFn: () =>
			adminApi.games.list({
				limit,
				offset,
				order,
				q: search,
				sort_by: sortBy,
				...rest,
			}),
		queryKey: queryKeys.games.list({ limit, offset, order, search, sortBy, ...rest }),
	});
}

export function useGame(id: string) {
	return useQuery({
		enabled: !!id,
		queryFn: () => adminApi.games.get(id),
		queryKey: queryKeys.games.detail(id),
	});
}

export function useUpdateGame() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: Game["id"]; data: Partial<Game> }) =>
			adminApi.games.update(id, data),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.games.all }),
	});
}

export function useDeleteGame() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.games.delete(id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.games.all }),
	});
}

/** Hard-delete several games in one request (`DELETE /admin/games?ids=…`). */
export function useDeleteGames() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (ids: string[]) => adminApi.games.deleteMany(ids),
		// Settled, not success: a rejected batch may still have deleted games.
		onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.games.all }),
	});
}

/** Bulk cancel and/or web kill-switch (`PATCH /admin/games?ids=…`). */
export function usePatchGames() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ ids, patch }: { ids: string[]; patch: GameBulkPatch }) =>
			adminApi.games.patchMany(ids, patch),
		// Settled, not success: a rejected batch may still have patched games.
		onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.games.all }),
	});
}

function invalidateGameVisibility(queryClient: ReturnType<typeof useQueryClient>) {
	return Promise.all([
		queryClient.invalidateQueries({ queryKey: queryKeys.games.all }),
		queryClient.invalidateQueries({ queryKey: queryKeys.moderation.all }),
	]);
}

/** Hide a game from players via moderation `rejected` (reversible with {@link useRestoreGame}). */
export function useHideGame() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.moderation.rejectGame(id),
		onSuccess: () => invalidateGameVisibility(queryClient),
	});
}

/** Make a hidden / pending game visible again via moderation `approved`. */
export function useRestoreGame() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.moderation.approveGame(id),
		onSuccess: () => invalidateGameVisibility(queryClient),
	});
}
